// The forms filled in against one job or one machine — and the ones that can
// be STARTED against it (round 28).
//
// Shown on the job detail page and the resource detail page. A prestart
// history is most useful next to the thing it is about — somebody asking "when
// was this excavator last checked" is looking at the excavator, not at a list
// of every form the company has ever filled in.
//
// The start links carry the job or machine as a PRESET in the URL, not a
// decision: the fill screen puts it in the picker only if the form offers it,
// and asks otherwise. A link must never be able to skip the question the
// picker exists to ask.
//
// Renders nothing at all when there is nothing to show AND nothing to start.
// A permanently empty panel on every job is noise on the page people open most.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formFill, type Fillable, type SubmissionListItem } from '../../formsApi';
import { DownloadLink } from '../DownloadLink';

export function LinkedSubmissions({
  jobId,
  resourceId,
  showWhenEmpty = false,
  heading = 'Forms',
}: {
  jobId?: string;
  resourceId?: string;
  /**
   * Render an empty state rather than nothing. A TAB has already promised
   * the reader there is something behind it, so it must answer — the
   * disappearing panel below is only right where the panel is one of many
   * stacked down a page.
   */
  showWhenEmpty?: boolean;
  /** Null inside a tab already named Forms — the heading would say it twice. */
  heading?: string | null;
}) {
  const [submissions, setSubmissions] = useState<SubmissionListItem[] | null>(null);
  const [mentions, setMentions] = useState<SubmissionListItem[]>([]);
  const [startable, setStartable] = useState<Fillable['templates']>([]);

  useEffect(() => {
    void formFill
      .list(jobId ? { jobId } : { resourceId })
      .then((result) => setSubmissions(result.submissions), () => setSubmissions([]));
    // MENTIONED, not filed against (round 28): the hard split, no toggle. A
    // day sheet naming four machines must not land in four prestart
    // histories — it lands here, in its own group, hidden when empty.
    void formFill
      .list(jobId ? { mentionsJobId: jobId } : { mentionsResourceId: resourceId })
      .then((result) => setMentions(result.submissions), () => setMentions([]));
    // Which forms this thing could have filled in against it: link_kind must
    // match the page (`any` forms have no target and start from the landing).
    const wanted = jobId ? 'job' : 'asset';
    void formFill.fillable().then(
      (result) => setStartable(result.templates.filter((t) => t.linkKind === wanted)),
      () => setStartable([])
    );
  }, [jobId, resourceId]);

  const empty =
    submissions !== null &&
    submissions.length === 0 &&
    startable.length === 0 &&
    mentions.length === 0;

  if (submissions === null || (empty && !showWhenEmpty)) return null;

  const presetQuery = jobId ? `?jobId=${jobId}` : `?resourceId=${resourceId}`;

  return (
    <section className="panel">
      {heading && (
        <div className="row mb-3">
          <h2 className="!mb-0">{heading}</h2>
        </div>
      )}

      {empty && (
        <p className="muted">
          No forms have been filled in against this job, and no form is set up to be started from
          one.
        </p>
      )}

      {startable.length > 0 && (
        <ul className="mb-3 m-0 list-none space-y-1 p-0">
          {startable.map((t) => (
            <li key={t.id}>
              <Link to={`/app/forms/fill/${t.id}${presetQuery}`} className="table-link text-sm">
                Fill in: {t.name}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {submissions.length > 0 && (
        <ul className="space-y-2">
          {submissions.map((row) => (
            <li key={row.id} className="rounded border border-gray-200 p-3">
              <Link
                to={
                  row.status === 'draft'
                    ? `/app/forms/fill?draft=${row.id}`
                    : `/app/forms/submissions/${row.id}`
                }
                className="table-link"
              >
                {row.templateName}
              </Link>
              <div className="text-sm text-gray-600">
                {row.submittedAt
                  ? `${row.submittedByName ?? 'Someone'} · ${new Date(
                      row.submittedAt
                    ).toLocaleDateString('en-AU')}`
                  : 'Not finished'}
              </div>
            </li>
          ))}
        </ul>
      )}

      {mentions.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Mentioned in these forms</h3>
          <ul className="space-y-2">
            {mentions.map((row) => (
              <li key={row.id} className="rounded border border-gray-200 p-3">
                <Link to={`/app/forms/submissions/${row.id}`} className="table-link">
                  {row.templateName}
                </Link>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>
                    {row.submittedByName ?? 'Someone'}
                    {row.submittedAt
                      ? ` · ${new Date(row.submittedAt).toLocaleDateString('en-AU')}`
                      : ''}
                  </span>
                  <DownloadLink href={formFill.pdfDownloadUrl(row.id)} className="table-link">
                    Download PDF
                  </DownloadLink>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
